import { WithApiRequestMeta } from "../../api-results";
import {
  PaginatedResponse,
  RtkEmptySplitApi,
  PaginatedRequestParams,
} from "../../types";
import { TasksApiTag } from "../tasks/tags";
import { TaskActivity } from "../tasks/types";
import { TasksCommentsApiTag } from "./tags";
import { TaskComment } from "./types";

export const TASK_COMMENTS_TAGS_CREATORS = {
  TASK_ID: (taskId: string) => ({
    type: TasksCommentsApiTag.taskComment,
    id: `TASK_ID-${taskId}`,
  }),
};

export const addTasksCommentsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [TasksCommentsApiTag.taskComment, TasksApiTag.taskActivity],
  });

  const tasksCommentsApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      getTaskComments: builder.query<
        PaginatedResponse<TaskComment>,
        WithApiRequestMeta<{
          entityId: string;
          taskId: string;
          repliesCommentId?: string;
          params?: PaginatedRequestParams<{ from?: string }>;
        }>
      >({
        query: ({ entityId, taskId, params, repliesCommentId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/comment${
            repliesCommentId ? `/${repliesCommentId}/reply` : ""
          }`,
          params,
        }),
        providesTags: (_result, _error, arg) => [
          TASK_COMMENTS_TAGS_CREATORS.TASK_ID(arg.taskId),
        ],
      }),

      getTaskComment: builder.query<
        { data: [TaskComment] },
        WithApiRequestMeta<{
          entityId: string;
          taskId: string;
          commentId: string;
          replyId?: string;
        }>
      >({
        query: ({ entityId, taskId, commentId, replyId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/comments/${commentId}${
            replyId ? `/reply/${replyId}` : ""
          }`,
        }),
      }),

      createTaskComment: builder.mutation<
        TaskActivity,
        WithApiRequestMeta<{
          taskId: string;
          entityId: string;
          replyToCommentId?: string;
          data: {
            content?: string;
            files?: File[];
          };
        }>
      >({
        query: ({ taskId, entityId, data, replyToCommentId }) => {
          const formData = new FormData();
          formData.append("content", data.content || "");
          data.files?.forEach((file) => {
            formData.append(
              data.files && data.files.length > 1 ? "files[]" : "files",
              file
            );
          });

          return {
            url: `/api/auth/entity/${entityId}/task/${taskId}/comments${
              replyToCommentId ? `/${replyToCommentId}/reply` : ""
            }`,
            method: "POST",
            data: formData,
          };
        },
      }),

      editComment: builder.mutation<
        TaskActivity,
        WithApiRequestMeta<{
          taskId: string;
          entityId: string;
          commentId: string;
          parentCommentId?: string;
          data: {
            content?: string;
            removedFiles?: string[];
          };
        }>
      >({
        query: ({ taskId, entityId, commentId, data }) => {
          const formData = new FormData();
          formData.append("content", data.content || "");
          data.removedFiles?.forEach((filePath) => {
            formData.append("removed_files[]", filePath);
          });

          return {
            url: `/api/auth/entity/${entityId}/task/${taskId}/comments/${commentId}`,
            method: "PUT",
            data: formData,
          };
        },
      }),

      deleteTaskComment: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          taskId: string;
          commentId: string;
          parentCommentId?: string;
        }>
      >({
        query: ({ entityId, taskId, commentId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/comments/${commentId}`,
          method: "DELETE",
        }),
      }),
    }),
  });

  return tasksCommentsApi;
};

export type TasksCommentsApi = ReturnType<typeof addTasksCommentsApi>;
